import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Gift, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Mascot } from '@/components/Mascot';
import { usePreferredSport } from '@/hooks/usePreferredSport';

export interface StreakMilestone {
  days: number;
  xpBonus: number;
  gemsBonus: number;
  label: string;
  emoji: string;
}

export const STREAK_MILESTONES: StreakMilestone[] = [
  { days: 3, xpBonus: 15, gemsBonus: 5, label: '3-Day Streak', emoji: '🔥' },
  { days: 7, xpBonus: 35, gemsBonus: 15, label: 'Weekly Warrior', emoji: '⚡' },
  { days: 14, xpBonus: 75, gemsBonus: 30, label: 'Two-Week Titan', emoji: '💪' },
  { days: 30, xpBonus: 150, gemsBonus: 75, label: 'Monthly MVP', emoji: '🏆' },
];

export function getReachedMilestone(streak: number): StreakMilestone | null {
  // Return the milestone that was JUST reached (exact match)
  return STREAK_MILESTONES.find(m => m.days === streak) ?? null;
}

interface StreakMilestonePopupProps {
  milestone: StreakMilestone;
  onClaim: (xp: number, gems: number) => void;
  onDismiss: () => void;
}

export function StreakMilestonePopup({ milestone, onClaim, onDismiss }: StreakMilestonePopupProps) {
  const { preferredSport } = usePreferredSport();
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6"
        onClick={onDismiss}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 30 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="bg-card border-2 border-accent/30 rounded-2xl shadow-elevated p-6 max-w-sm w-full text-center"
          onClick={e => e.stopPropagation()}
        >
          <button onClick={onDismiss} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>

          <Mascot mood="streak" sport={preferredSport} size={72} className="mx-auto mb-2" />

          <motion.div
            className="text-4xl mb-1"
            animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {milestone.emoji}
          </motion.div>

          <h3 className="font-display font-extrabold text-xl text-foreground">
            {milestone.label}!
          </h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            You've been learning for {milestone.days} days straight!
          </p>

          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="bg-primary/10 rounded-xl px-4 py-2 text-center">
              <span className="display-number text-lg text-primary">+{milestone.xpBonus}</span>
              <p className="text-[10px] text-muted-foreground font-display font-bold uppercase tracking-wider">XP</p>
            </div>
            <div className="bg-accent/10 rounded-xl px-4 py-2 text-center">
              <span className="display-number text-lg text-accent">+{milestone.gemsBonus}</span>
              <p className="text-[10px] text-muted-foreground font-display font-bold uppercase tracking-wider">Gems</p>
            </div>
          </div>

          <Button
            onClick={() => onClaim(milestone.xpBonus, milestone.gemsBonus)}
            className="w-full h-12 font-display font-bold text-base bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl"
          >
            <Gift className="w-5 h-5 mr-2" />
            Claim Reward
          </Button>

          {/* Next milestone preview */}
          {(() => {
            const next = STREAK_MILESTONES.find(m => m.days > milestone.days);
            return next ? (
              <p className="text-xs text-muted-foreground mt-3">
                Next milestone: <span className="font-display font-bold text-foreground">{next.label}</span> at {next.days} days
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-3 font-display font-bold">
                🎉 You've unlocked all streak milestones!
              </p>
            );
          })()}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

interface StreakMilestoneBarProps {
  currentStreak: number;
}

export function StreakMilestoneBar({ currentStreak }: StreakMilestoneBarProps) {
  const nextMilestone = STREAK_MILESTONES.find(m => m.days > currentStreak);
  if (!nextMilestone || currentStreak === 0) return null;

  const prevMilestone = [...STREAK_MILESTONES].reverse().find(m => m.days <= currentStreak);
  const prevDays = prevMilestone?.days ?? 0;
  const progress = ((currentStreak - prevDays) / (nextMilestone.days - prevDays)) * 100;

  return (
    <div className="bg-card rounded-xl border-2 border-border p-4 shadow-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-accent" />
          <span className="text-xs font-display font-bold text-foreground">Next Reward</span>
        </div>
        <span className="text-xs text-muted-foreground font-body">
          {nextMilestone.emoji} {nextMilestone.label} ({nextMilestone.days - currentStreak} days left)
        </span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-accent to-accent/70 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(progress, 100)}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-muted-foreground">
          +{nextMilestone.xpBonus} XP · +{nextMilestone.gemsBonus} Gems
        </span>
        <span className="text-[10px] font-display font-bold text-accent">
          Day {currentStreak}/{nextMilestone.days}
        </span>
      </div>
    </div>
  );
}
